import { DOM_IDS } from "@circle-react-uikit/CircleAIAppShell/Layout/defaults";
import { useIsMobile } from "@circleco/compass/hooks/useIsMobile";

import { useIsCircleAIShellMounted } from "@/react/components/Layout/CircleAIAppShellLayout/ShellBypassContext";
import { useIsConnectPopoverPanel } from "@/react/components/shared/Connect/ConnectPopoverContext";
import { CONNECT_PANEL_SCROLL_CONTAINER_ID } from "@/react/components/shared/uikit/HeaderV4/RightActionBlock/Connect/constants";

const LEGACY_PANEL_ID = "panel";

/**
 * Returns the DOM id of the current member-area scroll container.
 *
 * - Inside the header Connect popover: the popover's own scroller
 *   (`CONNECT_PANEL_SCROLL_CONTAINER_ID`), since the page does not scroll there.
 * - Inside the MobileShell (mobile viewport): `"panel"` (the id on
 *   `MobileShell.Content`'s `ScrollArea.Viewport`).
 * - Inside the AppShell: `DOM_IDS.workspaceBody` (the id on
 *   `CircleAIAppShell.Layout.Workspace.Body` and canvas-only content).
 * - Outside the AppShell (legacy StandardLayout V1/V2/Minimal):
 *   `"panel"` (the legacy id set on `CommunityContent` / ERB layouts).
 *
 * Use for `InfiniteScroll`'s `scrollableTarget`, `IntersectionObserver`
 * roots, programmatic `scrollTo`, and anywhere else that previously
 * hardcoded the `"panel"` string.
 *
 * When the legacy layouts are deleted, this hook collapses to
 * `() => DOM_IDS.workspaceBody` and can be inlined.
 */
export const useMemberAreaScrollContainerId = (): string => {
  const isMobile = useIsMobile();
  // Bypass routes (`?iframe=true`) render outside the AppShell, so check that
  // it is actually mounted.
  const isCircleAIShellMounted = useIsCircleAIShellMounted();
  const isConnectPopoverPanel = useIsConnectPopoverPanel();

  // The Connect popover scrolls inside its own panel, not the page.
  if (isConnectPopoverPanel) return CONNECT_PANEL_SCROLL_CONTAINER_ID;

  // MobileShell uses id="panel" on its ScrollArea.Viewport.
  if (isMobile) return LEGACY_PANEL_ID;

  return isCircleAIShellMounted ? DOM_IDS.workspaceBody : LEGACY_PANEL_ID;
};
